import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../../common';
import { BaseConfirmModal } from '../../../../common/components/modals';
import { HistoryRequestsByAuthorizerModel } from '../../../../models/requests-management/historyRequestByAuthorizer';
import { RequestsManagementsAPI } from '../../../../services/external/APIs/requests-managements/RequestsManagements';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

export interface GetSchedulingsChangeRequestsHistoryRequest {
    status: number | null;
    startDate: string;
    endDate: string;
}

export const RequestHistoryModal = () => {
    const { isRequestHistoryModalOpen, setIsRequestHistoryModalOpen, lang } =
        useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [historyRequest, setHistoryRequest] =
        useState<GetSchedulingsChangeRequestsHistoryRequest>({
            endDate: '',
            startDate: '',
            status: 0,
        });

    const [response, setResponse] = useState({} as HistoryRequestsByAuthorizerModel);
    const [responseCopy, setResponseCopy] = useState(
        {} as HistoryRequestsByAuthorizerModel
    );

    const onFilter = (value: string) => {
        setSearchTerm(value);

        if (value.length == 0) {
            setResponseCopy(response);
            return;
        }

        let workshiftsCopy = response.workshiftSchedulingChangeRequests.filter(
            (r) =>
                r.workshiftName.toLowerCase().includes(value.toLowerCase()) ||
                r.documentNumber.toLowerCase().includes(value.toLowerCase()) ||
                r.authorizerName.toLowerCase().includes(value.toLowerCase()) ||
                r.fullName.toLowerCase().includes(value.toLowerCase())
        );

        let interruptionsCopy = response.interruptionRequests.filter(
            (r) =>
                r.interruptionTypeName.toLowerCase().includes(value.toLowerCase()) ||
                r.documentNumber.toLowerCase().includes(value.toLowerCase()) ||
                r.authorizerName.toLowerCase().includes(value.toLowerCase()) ||
                r.fullName.toLowerCase().includes(value.toLowerCase())
        );

        setResponseCopy({
            interruptionRequests: interruptionsCopy,
            workshiftSchedulingChangeRequests: workshiftsCopy,
        });
    };

    const onSearch = () => {
        const id = toast.loading('Buscando...');

        let body: GetSchedulingsChangeRequestsHistoryRequest = {
            endDate: historyRequest.endDate,
            startDate: historyRequest.startDate,
            status:
                historyRequest.status == 0 ? null : Number(historyRequest.status),
        };

        RequestsManagementsAPI.getSchedulingChangeRequestByAuthorizersFiltered(body)
            .then((response) => {
                setResponse(response);
                setResponseCopy(response);
                toast.update(id, {
                    render: 'Busqueda completada',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            })
            .catch((e) => {
                toast.update(id, {
                    render: 'error de busqueda',
                    type: 'warning',
                    isLoading: false,
                    autoClose: 5000,
                    position: toast.POSITION.BOTTOM_RIGHT,
                    closeOnClick: true,
                    closeButton: true,
                });
            })
            .finally(() => setIsLoading(false));
    };

    const onCancel = () => {
        setIsRequestHistoryModalOpen(false);
    };

    const handleInputChange = (
        name: string,
        value: string | boolean | string[] | number
    ) => {
        setHistoryRequest({ ...historyRequest, [name]: value });
    };

    const getStatusName = (status: number | null) => {
        let statusList = [
            { label: 'Pendiente', value: 1 },
            { label: 'Rechazado', value: 2 },
            { label: 'Aprobado', value: 3 },
            { label: 'Cancelado', value: 4 },
            { label: 'Todos', value: 0 },
        ];

        return statusList.find((v) => v.value == status)?.label;
    };

    const getStatusClassName = (status: number | null) => {
        let className = `${status == 1 && 'bg-primary'} ${
            status == 2 && 'bg-info'
        } ${status == 3 && 'bg-success'} 
        `;

        return className;
    };

    return (
        <BaseConfirmModal
            isOpen={isRequestHistoryModalOpen}
            onSubmit={onCancel}
            onClose={onCancel}
            modalTitle='Historial de solicitudes por Autorizador'
            isLoading={false}
            isShowControls={false}
            disableSubmit={
                historyRequest.endDate == '' || historyRequest.startDate == ''
            }
        >
            <div className='modal-body pb-1 '>
                <div className='hstack gap-2'>
                    <input
                        type='text'
                        className='form-control text-dark '
                        value={searchTerm}
                        placeholder='Buscar...'
                        onChange={(e) => onFilter(e.target.value)}
                    />

                    <select
                        className='form-select text-dark'
                        value={historyRequest.status ?? 0}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                        <option value='1'>Pendiente</option>
                        <option value='2'>Rechazado</option>
                        <option value='3'>Aprobado</option>
                        <option value='4'>Cancelado</option>
                        <option value='0'>Todos</option>
                    </select>

                    <input
                        type='date'
                        className='form-control text-dark'
                        value={historyRequest.startDate}
                        onChange={(e) =>
                            handleInputChange('startDate', e.target.value)
                        }
                    />

                    <input
                        type='date'
                        className='form-control text-dark'
                        value={historyRequest.endDate}
                        onChange={(e) =>
                            handleInputChange('endDate', e.target.value)
                        }
                    />

                    <div className=''>
                        <button
                            className='btn btn-primary '
                            onClick={(e) => onSearch()}
                            disabled={
                                historyRequest.endDate == '' ||
                                historyRequest.startDate == ''
                            }
                        >
                            <i className=' ri-search-line'></i>
                        </button>
                    </div>
                </div>

                <hr />

                <div>
                    <div className='text-muted mb-3'>
                        {responseCopy?.workshiftSchedulingChangeRequests && (
                            <p className='m-0 p-0'>
                                Solicitudes de turnos:{' '}
                                {
                                    responseCopy?.workshiftSchedulingChangeRequests
                                        ?.length
                                }
                            </p>
                        )}

                        {responseCopy?.interruptionRequests && (
                            <p className='m-0 p-0'>
                                Solicitudes de permisos:{' '}
                                {responseCopy?.interruptionRequests?.length}
                            </p>
                        )}
                    </div>


                    <div className='accordion text-black' id='accordionExample'>
                        {responseCopy?.workshiftSchedulingChangeRequests?.length >
                            0 && (
                            <div className='accordion-item'>
                                <h2 className='accordion-header text-primary2 fw-bold'>
                                    <button
                                        className='accordion-button text-primary2 fw-bold'
                                        type='button'
                                        data-bs-toggle='collapse'
                                        data-bs-target='#collapseOne'
                                        aria-expanded='true'
                                        aria-controls='collapseOne'
                                    >
                                        Solicitudes de turnos
                                    </button>
                                </h2>

                                <div
                                    id='collapseOne'
                                    className='accordion-collapse collapse show'
                                    data-bs-parent='#accordionExample'
                                >
                                    <div className='accordion-body'>
                                        <SimpleBar
                                            style={{ maxHeight: 400 }}
                                            direction='ltr'
                                        >
                                            <div className='vstack gap-2'>
                                                {responseCopy.workshiftSchedulingChangeRequests.map(
                                                    (wsc) => (
                                                        <div
                                                            className='border rounded border-dashed p-2'
                                                            key={
                                                                wsc.workshiftSchedulingChangeRequestId
                                                            }
                                                        >
                                                            <div className='d-flex align-items-center'>
                                                                <div className='flex-grow-1 overflow-hidden'>
                                                                    <h5 className='fs-13 mb-0 '>
                                                                        {wsc.fullName +
                                                                            ' ' +
                                                                            wsc.documentNumber}{' '}
                                                                        <div className=''>
                                                                            <span
                                                                                className={`badge ${getStatusClassName(
                                                                                    wsc.status
                                                                                )}`}
                                                                            >
                                                                                {getStatusName(
                                                                                    wsc.status
                                                                                )}
                                                                            </span>
                                                                            {wsc.authorizerName && (
                                                                                <div className='text-muted'>
                                                                                    {' '}
                                                                                    Por:{' '}
                                                                                    {' ' +
                                                                                        wsc.authorizerName}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </h5>
                                                                    <div className='text-muted'>
                                                                        {
                                                                            wsc.date.split(
                                                                                'T'
                                                                            )[0]
                                                                        }
                                                                    </div>
                                                                </div>

                                                                <div className='flex-shrink-0 ms-2 d-flex gap-2'>
                                                                    <div className='d-flex gap-1'>
                                                                        {
                                                                            wsc.workshiftName
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </SimpleBar>
                                    </div>
                                </div>
                            </div>
                        )}

                        {responseCopy?.interruptionRequests?.length > 0 && (
                            <div className='accordion-item'>
                                <h2 className='accordion-header'>
                                    <button
                                        className='accordion-button collapsed text-primary2 fw-bold'
                                        type='button'
                                        data-bs-toggle='collapse'
                                        data-bs-target='#collapseTwo'
                                        aria-expanded='false'
                                        aria-controls='collapseTwo'
                                    >
                                        Solicitudes de permisos
                                    </button>
                                </h2>
                                <div
                                    id='collapseTwo'
                                    className='accordion-collapse collapse show'
                                    data-bs-parent='#accordionExample2'
                                >
                                    <div className='accordion-body'>
                                        <SimpleBar
                                            style={{ maxHeight: 400 }}
                                            direction='rtl'
                                        >
                                            <div className='vstack gap-2'>
                                                {responseCopy.interruptionRequests.map(
                                                    (wsc) => (
                                                        <div
                                                            className='border rounded border-dashed p-2'
                                                            key={
                                                                wsc.interruptionRequestId
                                                            }
                                                        >
                                                            <div className='d-flex align-items-center'>
                                                                <div className='flex-grow-1 overflow-hidden'>
                                                                    <h5 className='fs-13 mb-0'>
                                                                        {wsc.fullName +
                                                                            ' ' +
                                                                            wsc.documentNumber}{' '}
                                                                        <div className=''>
                                                                            <span
                                                                                className={`badge ${getStatusClassName(
                                                                                    wsc.status
                                                                                )}`}
                                                                            >
                                                                                {getStatusName(
                                                                                    wsc.status
                                                                                )}
                                                                            </span>
                                                                            {wsc.authorizerName && (
                                                                                <div className='text-muted'>
                                                                                    {' '}
                                                                                    Por:{' '}
                                                                                    {' ' +
                                                                                        wsc.authorizerName}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </h5>
                                                                    <div className='text-muted'>
                                                                        {
                                                                            wsc.date.split(
                                                                                'T'
                                                                            )[0]
                                                                        }
                                                                    </div>
                                                                </div>

                                                                <div className='flex-shrink-0 ms-2 d-flex gap-2'>
                                                                    <div className='d-flex gap-1'>
                                                                        {
                                                                            wsc.interruptionTypeName
                                                                        }
                                                                    </div>
                                                                    {wsc.fileUrl && (
                                                                        <a
                                                                            className='btn btn-info btn-sm'
                                                                            href={`${process.env.REACT_APP_API_URL}jerusalem/api/v2.0.0/files/${wsc.interruptionRequestId}/file`}
                                                                            target='_blank'
                                                                            rel='noopener noreferrer'
                                                                        >
                                                                            <i className='ri-file-download-line'></i> Descargar
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </SimpleBar>

                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </BaseConfirmModal>
    );
};
